import React, { useCallback } from "react";
import { connect } from "react-redux";

import { defineMessages, useIntl } from "react-intl";

import BoundaryType from "@mapmycustomers/shared/enum/BoundaryType";
import useChangeTracking from "@mapmycustomers/shared/util/hook/useChangeTracking";

import { ReactComponent as BoundariesIcon } from "@app/assets/map/legends/boundaries/boundariesDisabled.svg";
import { ReactComponent as DemographicIcon } from "@app/assets/map/legends/geographicLinesDisabled.svg";
import { ReactComponent as TerritoriesIcon } from "@app/assets/map/legends/territoriesDisabled.svg";
import ButtonLink from "@app/component/ButtonLink";
import RadioGroup from "@app/component/input/RadioGroup";
import { MapLayer } from "@app/scene/map/store";
import { setBoundaryType, toggleLayerVisibility } from "@app/scene/map/store/layers/actions";
import { getBoundaryType } from "@app/scene/map/store/layers/boundaries/selectors";
import { doesOrganizationSupportTerritories } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";

import LegendCard, { BaseLegendCardProps } from "../LegendCard";

import styles from "./BoundariesLegendCard.module.scss";
import Demography from "./components/Demography";
import Territories from "./components/Territories";

const messages = defineMessages({
  demography: {
    id: "map.layer.boundaries.radio.demography",
    defaultMessage: "Geography",
    description: "Title of the Geographic Lines Radio button",
  },
  legend: {
    id: "map.layer.boundaries.legend",
    defaultMessage: "Boundaries",
    description: "Title of the Territories' legend",
  },
  message: {
    id: "map.layer.boundaries.disabledState.message",
    defaultMessage: "Visualize either Territory or Geographic Boundaries on the map",
    description: "Description of the Boundaries legend disabled state",
  },
  onlyBoundaries: {
    id: "map.layer.boundaries.disabledState.onlyBoundaries",
    defaultMessage: "Visualize Geographic Boundaries on the map",
    description: "Description of the Boundaries legend disabled state",
  },
  option: {
    id: "map.layer.boundaries.enable.option",
    defaultMessage: "Enable Boundaries",
    description: "Option for users to Enable Boundaries layer",
  },
  territory: {
    id: "map.layer.boundaries.radio.territory",
    defaultMessage: "Territory",
    description: "Title of the Territories Radio button",
  },
});

interface Props extends BaseLegendCardProps {
  activeBoundaryType: BoundaryType;
  onSetBoundaryType: typeof setBoundaryType;
  onToggleLayerVisibility: (payload: { name: MapLayer; visible: boolean }) => void;
  territoriesEnabled?: boolean;
}

const BoundariesLegendCard: React.FC<Props> = ({
  activeBoundaryType,
  onSetBoundaryType,
  onToggleLayerVisibility,
  territoriesEnabled,
  ...props
}) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const handleLegendVisibilityChange = useCallback(
    (value: boolean) => {
      analytics.clicked(["User boundaries toggle on"], { status: value ? "on" : "off" });
      onToggleLayerVisibility({ name: "boundaries", visible: value });
    },
    [analytics, onToggleLayerVisibility]
  );

  const handleChange = useCallback(
    (key) => {
      onSetBoundaryType(key);
      analytics.using(["Boundaries", key]);
    },
    [analytics, onSetBoundaryType]
  );

  useChangeTracking(() => {
    if (props.active) {
      analytics.clicked([]);
    }
  }, [analytics, props.active]);

  const options = [
    {
      label: (
        <span className={styles.radioOption}>
          <TerritoriesIcon className={styles.radioIcon} />
          {intl.formatMessage(messages.territory)}
        </span>
      ),
      value: BoundaryType.TERRITORY,
    },
    {
      label: (
        <span className={styles.radioOption}>
          <DemographicIcon className={styles.radioIcon} />
          {intl.formatMessage(messages.demography)}
        </span>
      ),
      value: BoundaryType.DEMOGRAPHY,
    },
  ];

  return (
    <LegendCard
      {...props}
      icon={<BoundariesIcon />}
      isLayer={props.isLayer}
      onVisibilityChange={handleLegendVisibilityChange}
      title={intl.formatMessage(messages.legend)}
    >
      {props.visible ? (
        <div className={styles.container}>
          {territoriesEnabled && (
            <RadioGroup
              className={styles.switch}
              onChange={handleChange}
              options={options}
              optionType="button"
              value={activeBoundaryType}
            />
          )}
          {activeBoundaryType === BoundaryType.TERRITORY && territoriesEnabled ? (
            <Territories />
          ) : (
            <Demography />
          )}
        </div>
      ) : (
        <div className={styles.disabledLayerContainer}>
          <div className={styles.disabledLayer}>{<BoundariesIcon />}</div>
          <div className={styles.EnableLegendMessage}>
            {intl.formatMessage(territoriesEnabled ? messages.message : messages.onlyBoundaries)}
          </div>
          <ButtonLink onClick={() => handleLegendVisibilityChange(true)}>
            {intl.formatMessage(messages.option)}
          </ButtonLink>
        </div>
      )}
    </LegendCard>
  );
};

const mapStateToProps = (state: RootState) => ({
  activeBoundaryType: getBoundaryType(state),
  territoriesEnabled: doesOrganizationSupportTerritories(state),
});

const mapDispatchToProps = {
  onSetBoundaryType: setBoundaryType,
  onToggleLayerVisibility: toggleLayerVisibility,
};

export default connect(mapStateToProps, mapDispatchToProps)(BoundariesLegendCard);
